import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function Hero() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.5
  });
  const [animatedValue, setAnimatedValue] = useState(41);
  useEffect(() => {
    if (isInView) {
      const duration = 1200;
      const start = 41;
      const end = 85;
      const startTime = performance.now();
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        setAnimatedValue(Math.round(start + (end - start) * easeProgress));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      const timeout = setTimeout(() => {
        requestAnimationFrame(animate);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isInView]);
  return <section className="min-h-[100vh] pt-[72px] bg-arena flex items-center" data-id="element-196">
      <div className="max-w-[1200px] mx-auto w-full px-6 md:px-20 py-12 md:py-0" data-id="element-197">
        <div className="grid grid-cols-1 md:grid-cols-[55%_45%] gap-12 md:gap-8 items-center min-h-[calc(100vh-72px)]" data-id="element-198">
          {/* Left Column */}
          <div className="flex flex-col items-start" data-id="element-199">
            <span className="font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-200">
              SITIOS WEB ESTRATÉGICOS PARA EMPRESAS B2B
            </span>

            <h1 className="font-display text-[48px] md:text-[72px] font-bold text-carbon leading-[1.05] tracking-[-0.02em] max-w-[580px] mb-6" data-id="element-201">
              Tu empresa vale más
              <br data-id="element-202" />
              de lo que tu sitio
              <br data-id="element-203" />
              web está comunicando.
            </h1>

            <p className="font-body text-[20px] text-carbon/65 leading-[1.65] max-w-[480px] mb-10" data-id="element-204">
              Medimos exactamente qué está fallando en tu sitio web con el
              ICD™. Luego construimos uno que sí comunique lo que tu empresa
              vale.
            </p>

            <Link to="/diagnostico" className="inline-flex items-center justify-center bg-verde text-arena font-display font-semibold text-[15px] px-7 py-3.5 rounded-[6px] hover:bg-verde-hover hover:-translate-y-[1px] hover:shadow-cta active:translate-y-0 transition-all duration-200 w-full md:w-auto" data-id="element-205">
              Diagnosticar mi sitio web{' '}
              <ArrowRight size={16} className="ml-2" data-id="element-206" />
            </Link>

            <a href="#metodologia" className="hidden md:inline-block font-body text-[14px] text-carbon/45 hover:text-carbon/80 transition-opacity duration-200 mt-3" data-id="element-207">
              ¿Cómo construimos tu sitio web?
            </a>

            <p className="font-body text-[13px] text-carbon/35 mt-4" data-id="element-208">
              El diagnóstico tarda menos de 2 minutos en solicitarse.
            </p>
          </div>

          {/* Right Column - Visualization */}
          <div className="w-full flex flex-col justify-center h-full" ref={ref} data-id="element-209">
            <span className="block font-body text-[11px] uppercase text-salvia tracking-[0.15em] mb-4" data-id="element-210">
              LO QUE TU SITIO WEB COMUNICA HOY
            </span>

            {/* Bar 1 */}
            <div className="mb-6" data-id="element-211">
              <div className="flex justify-between items-end mb-2" data-id="element-212">
                <span className="font-body text-[13px] text-carbon/60" data-id="element-213">
                  Lo que tu empresa realmente vale
                </span>
                <span className="font-body text-[13px] font-semibold text-verde" data-id="element-214">
                  100%
                </span>
              </div>
              <div className="h-2 w-full bg-verde rounded-full" data-id="element-215"></div>
            </div>

            {/* Bar 2 (Animated) */}
            <div className="mb-6" data-id="element-216">
              <div className="flex justify-between items-end mb-2" data-id="element-217">
                <span className="font-body text-[13px] text-carbon/60" data-id="element-218">
                  Lo que tu sitio web comunica
                </span>
                <span className="font-body text-[13px] font-semibold text-azul" data-id="element-219">
                  {animatedValue}%
                </span>
              </div>
              <div className="h-2 w-full bg-azul/15 rounded-full overflow-hidden" data-id="element-220">
                <motion.div className="h-full bg-azul rounded-full" initial={{
                width: '41%'
              }} animate={isInView ? {
                width: '85%'
              } : {
                width: '41%'
              }} transition={{
                duration: 1.2,
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-221" />
              </div>
            </div>

            {/* Gap Indicator */}
            <div className="mt-6 mb-8 flex items-center" data-id="element-222">
              <div className="text-carbon/40 text-[10px]" data-id="element-223">◄</div>
              <div className="flex-grow border-t-2 border-dashed border-carbon/20" data-id="element-224"></div>
              <div className="bg-arena px-3 py-1 font-display text-[12px] font-semibold text-carbon z-10 relative -mx-2" data-id="element-225">
                Brecha de Confianza Digital™: 59 puntos
              </div>
              <div className="flex-grow border-t-2 border-dashed border-carbon/20" data-id="element-226"></div>
              <div className="text-carbon/40 text-[10px]" data-id="element-227">►</div>
            </div>

            <p className="font-body text-[13px] text-carbon/50 leading-relaxed mt-2 text-left" data-id="element-228">
              Mientras esta brecha exista, tu empresa pierde oportunidades ante
              competidores que quizás no son mejores que tú.
            </p>
          </div>
        </div>
      </div>
    </section>;
}